import { Injectable, signal, computed, inject } from '@angular/core';
import { EntityType } from '../objects/interface-library';
import { UsersService } from './entity-services/users.service';
import { Contract } from '../objects/contract';
import { Coi } from '../objects/coi';
import { Baa } from '../objects/baa';
import { CriticalDate } from '../objects/critical-dates';
import { Provision } from '../objects/provision';
import moment from 'moment';

/**
 * Filter types available in My Work view
 */
export enum MyWorkFilter {
  Owner = 'Owner',
  ExpiringSoon = 'ExpiringSoon',
  Expired = 'Expired',
  NextReviewDateSoon = 'NextReviewDateSoon',
  PendingMyAction = 'PendingMyAction',
  TaskingIAssigned = 'TaskingIAssigned'
}

/**
 * Service to manage My Work component state using Angular signals
 * Handles entity type selection, filter configuration, and user-specific filtering
 */
@Injectable({
  providedIn: 'root'
})
export class MyWorkFilterService {
  private usersService = inject(UsersService);

  // Signal for currently selected entity type
  public selectedEntityType = signal<EntityType>(EntityType.Contract);

  // Signal for active filter selections
  public activeFilters = signal<MyWorkFilter[]>([]);

  // Signal for current user email
  public currentUserEmail = signal<string | null>(null);

  // Computed signal indicating whether any filters are active
  public hasActiveFilters = computed(() => this.activeFilters().length > 0);

  constructor() {
    // Subscribe to user email changes
    this.usersService.currentUserEmail$.subscribe(email => {
      this.currentUserEmail.set(email);
    });
  }

  /**
   * Sets the currently selected entity type
   */
  setEntityType(entityType: EntityType): void {
    this.selectedEntityType.set(entityType);
  }

  /**
   * Toggles a filter on/off
   */
  toggleFilter(filter: MyWorkFilter): void {
    const current = this.activeFilters();
    const index = current.indexOf(filter);

    if (index > -1) {
      const newFilters = current.filter(f => f !== filter);
      this.activeFilters.set(newFilters);
    } else {
      const newFilters = [...current, filter];
      this.activeFilters.set(newFilters);
    }
  }

  /**
   * Adds a filter to the active filters
   */
  addFilter(filter: MyWorkFilter): void {
    const current = this.activeFilters();
    if (!current.includes(filter)) {
      this.activeFilters.set([...current, filter]);
    }
  }

  /**
   * Removes a filter from active filters
   */
  removeFilter(filter: MyWorkFilter): void {
    this.activeFilters.set(this.activeFilters().filter(f => f !== filter));
  }

  /**
   * Clears all active filters
   */
  clearFilters(): void {
    this.activeFilters.set([]);
  }

  /**
   * Checks if a specific filter is active
   */
  isFilterActive(filter: MyWorkFilter): boolean {
    return this.activeFilters().includes(filter);
  }

  /**
   * Filters contracts based on active filters and current user
   * Returns array of contracts that match ANY active filter (OR logic)
   */
  filterContracts(contracts: Contract[]): Contract[] {
    const userEmail = this.currentUserEmail();
    const filters = this.activeFilters();

    if (!userEmail || filters.length === 0) {
      return [];
    }

    // Collect all contracts that match ANY filter (OR logic)
    const matchingContracts = new Set<Contract>();

    filters.forEach(filter => {

      switch (filter) {
        case MyWorkFilter.Owner:
          // Add all contracts where user is an owner
          let ownerCount = 0;
          contracts.forEach(contract => {
            if (this.isUserOwnerOfContract(contract, userEmail)) {
              matchingContracts.add(contract);
              ownerCount++;
            }
          });
          // console.log(`Found ${ownerCount} contracts where user is owner`);
          break;

        case MyWorkFilter.ExpiringSoon:
          // Add all contracts expiring in the next 6 months
          let expiringSoonCount = 0;
          contracts.forEach(contract => {
            if (contract.expirationDate) {
              const expirationDate = moment(contract.expirationDate);
              const now = moment();
              const daysDifferent = expirationDate.diff(now, 'days');
              if (daysDifferent > 0 && daysDifferent < 181) { // 180 days = ~6 months
                matchingContracts.add(contract);
                expiringSoonCount++;
              }
            }
          });
          // console.log(`Found ${expiringSoonCount} contracts expiring soon`);
          break;

        case MyWorkFilter.Expired:
          // Add all contracts that are already expired
          let expiredCount = 0;
          contracts.forEach(contract => {
            if (contract.expirationDate) {
              const expirationDate = moment(contract.expirationDate);
              const now = moment();
              const daysDifferent = expirationDate.diff(now, 'days');
              if (daysDifferent < 0) {
                matchingContracts.add(contract);
                expiredCount++;
              }
            }
          });
          // console.log(`Found ${expiredCount} expired contracts`);
          break;

        case MyWorkFilter.NextReviewDateSoon:
          // Add all contracts with next review date in the next 6 months
          let nextReviewDateSoonCount = 0;
          contracts.forEach(contract => {
            if (contract.nextReviewDate) {
              const nextReviewDate = moment(contract.nextReviewDate);
              const now = moment();
              const daysDifferent = nextReviewDate.diff(now, 'days');
              if (daysDifferent > 0 && daysDifferent < 181) { // 180 days = ~6 months
                matchingContracts.add(contract);
                nextReviewDateSoonCount++;
              }
            }
          });
          // console.log(`Found ${nextReviewDateSoonCount} contracts with next review date soon`);
          break;

        default:
          break;
      }
    });

    const result = Array.from(matchingContracts);
    // console.log(`Total matching contracts (after OR): ${result.length}`);
    return result;
  }

  /**
   * Checks if the given user is an owner of the contract
   * Checks internalSignatory, primaryOwner, secondaryOwner, tertiaryOwner, yourContractCoordinator
   */
  private isUserOwnerOfContract(contract: Contract, userEmail: string): boolean {
    const matchesEmail = (person: any, fieldName: string) => this.matchesUserEmail(person, userEmail, fieldName);

    // Log the contract being checked (only first time to reduce noise)
    const contractAny = contract as any;

    // Check all owner fields
    const isOwner = (
      matchesEmail(contract.internalSignatory, 'internalSignatory') ||
      matchesEmail(contractAny.primaryOwner, 'primaryOwner') ||
      matchesEmail(contractAny.secondaryOwner, 'secondaryOwner') ||
      matchesEmail(contractAny.tertiaryOwner, 'tertiaryOwner') ||
      matchesEmail(contractAny.yourContractCoordinator, 'yourContractCoordinator')
    );

    return isOwner;
  }

  /**
   * Filters COIs based on active filters and current user
   * Returns array of COIs that match ANY active filter (OR logic)
   */
  filterCois(cois: Coi[]): Coi[] {
    const userEmail = this.currentUserEmail();
    const filters = this.activeFilters();

    if (!userEmail || filters.length === 0) {
      return [];
    }

    // Collect all COIs that match ANY filter (OR logic)
    const matchingCois = new Set<Coi>();

    filters.forEach(filter => {

      switch (filter) {
        case MyWorkFilter.Owner:
          // Add all COIs where user is an owner
          let ownerCount = 0;
          cois.forEach(coi => {
            if (this.isUserOwnerOfCoi(coi, userEmail)) {
              matchingCois.add(coi);
              ownerCount++;
            }
          });
          // console.log(`Found ${ownerCount} COIs where user is owner`);
          break;

        case MyWorkFilter.ExpiringSoon:
          // Add all COIs expiring in the next 6 months
          let expiringSoonCount = 0;
          cois.forEach(coi => {
            if (coi.expirationDate) {
              const expirationDate = moment(coi.expirationDate);
              const now = moment();
              const daysDifferent = expirationDate.diff(now, 'days');
              if (daysDifferent > 0 && daysDifferent < 181) { // 180 days = ~6 months
                matchingCois.add(coi);
                expiringSoonCount++;
              }
            }
          });
          // console.log(`Found ${expiringSoonCount} COIs expiring soon`);
          break;

        case MyWorkFilter.Expired:
          // Add all COIs that are already expired
          let expiredCount = 0;
          cois.forEach(coi => {
            if (coi.expirationDate) {
              const expirationDate = moment(coi.expirationDate);
              const now = moment();
              const daysDifferent = expirationDate.diff(now, 'days');
              if (daysDifferent < 0) {
                matchingCois.add(coi);
                expiredCount++;
              }
            }
          });
          // console.log(`Found ${expiredCount} expired COIs`);
          break;

        case MyWorkFilter.NextReviewDateSoon:
          // Add all COIs with next review date in the next 6 months
          let nextReviewDateSoonCount = 0;
          cois.forEach(coi => {
            if (coi.nextReviewDate) {
              const nextReviewDate = moment(coi.nextReviewDate);
              const now = moment();
              const daysDifferent = nextReviewDate.diff(now, 'days');
              if (daysDifferent > 0 && daysDifferent < 181) { // 180 days = ~6 months
                matchingCois.add(coi);
                nextReviewDateSoonCount++;
              }
            }
          });
          // console.log(`Found ${nextReviewDateSoonCount} COIs with next review date soon`);
          break;

        default:
          break;
      }
    });

    const result = Array.from(matchingCois);
    // console.log(`Total matching COIs (after OR): ${result.length}`);
    return result;
  }

  /**
   * Checks if the given user is an owner of the COI
   * Checks internalCoordinator, internalSignatory, primaryOwner, secondaryOwner
   */
  private isUserOwnerOfCoi(coi: Coi, userEmail: string): boolean {
    const matchesEmail = (person: any, fieldName: string) => this.matchesUserEmail(person, userEmail, fieldName);

    const coiAny = coi as any;

    // Check all owner fields for COI
    const isOwner = (
      matchesEmail(coiAny.internalCoordinator, 'internalCoordinator') ||
      matchesEmail(coiAny.internalSignatory, 'internalSignatory') ||
      matchesEmail(coiAny.primaryOwner, 'primaryOwner') ||
      matchesEmail(coiAny.secondaryOwner, 'secondaryOwner')
    );

    return isOwner;
  }

  /**
   * Filters BAAs based on active filters and current user
   * Returns array of BAAs that match ANY active filter (OR logic)
   */
  filterBaas(baas: Baa[]): Baa[] {
    const userEmail = this.currentUserEmail();
    const filters = this.activeFilters();

    if (!userEmail || filters.length === 0) {
      return [];
    }

    // Collect all BAAs that match ANY filter (OR logic)
    const matchingBaas = new Set<Baa>();

    filters.forEach(filter => {

      switch (filter) {
        case MyWorkFilter.Owner:
          // Add all BAAs where user is an owner
          let ownerCount = 0;
          baas.forEach(baa => {
            if (this.isUserOwnerOfBaa(baa, userEmail)) {
              matchingBaas.add(baa);
              ownerCount++;
            }
          });
          // console.log(`Found ${ownerCount} BAAs where user is owner`);
          break;

        case MyWorkFilter.ExpiringSoon:
          // Add all BAAs expiring in the next 6 months
          let expiringSoonCount = 0;
          baas.forEach(baa => {
            if (baa.expirationDate) {
              const expirationDate = moment(baa.expirationDate);
              const now = moment();
              const daysDifferent = expirationDate.diff(now, 'days');
              if (daysDifferent > 0 && daysDifferent < 181) { // 180 days = ~6 months
                matchingBaas.add(baa);
                expiringSoonCount++;
              }
            }
          });
          // console.log(`Found ${expiringSoonCount} BAAs expiring soon`);
          break;

        case MyWorkFilter.Expired:
          // Add all BAAs that are already expired
          let expiredCount = 0;
          baas.forEach(baa => {
            if (baa.expirationDate) {
              const expirationDate = moment(baa.expirationDate);
              const now = moment();
              const daysDifferent = expirationDate.diff(now, 'days');
              if (daysDifferent < 0) {
                matchingBaas.add(baa);
                expiredCount++;
              }
            }
          });
          // console.log(`Found ${expiredCount} expired BAAs`);
          break;

        case MyWorkFilter.NextReviewDateSoon:
          // Add all BAAs with next review date in the next 6 months
          let nextReviewDateSoonCount = 0;
          baas.forEach(baa => {
            if (baa.nextReviewDate) {
              const nextReviewDate = moment(baa.nextReviewDate);
              const now = moment();
              const daysDifferent = nextReviewDate.diff(now, 'days');
              if (daysDifferent > 0 && daysDifferent < 181) { // 180 days = ~6 months
                matchingBaas.add(baa);
                nextReviewDateSoonCount++;
              }
            }
          });
          // console.log(`Found ${nextReviewDateSoonCount} BAAs with next review date soon`);
          break;

        default:
          break;
      }
    });

    const result = Array.from(matchingBaas);
    // console.log(`Total matching BAAs (after OR): ${result.length}`);
    return result;
  }

  /**
   * Checks if the given user is an owner of the BAA
   * Checks primaryOwner, secondaryOwner, tertiaryOwner
   */
  private isUserOwnerOfBaa(baa: Baa, userEmail: string): boolean {
    const matchesEmail = (person: any, fieldName: string) => this.matchesUserEmail(person, userEmail, fieldName);

    const baaAny = baa as any;

    // Check all owner fields for BAA
    const isOwner = (
      matchesEmail(baaAny.primaryOwner, 'primaryOwner') ||
      matchesEmail(baaAny.secondaryOwner, 'secondaryOwner') ||
      matchesEmail(baaAny.tertiaryOwner, 'tertiaryOwner')
    );

    return isOwner;
  }

  /**
   * Filters Critical Dates based on active filters and current user
   * Returns array of Critical Dates that match ANY active filter (OR logic)
   */
  filterCriticalDates(dates: CriticalDate[]): CriticalDate[] {
    const userEmail = this.currentUserEmail();
    const filters = this.activeFilters();

    if (!userEmail || filters.length === 0) {
      return [];
    }

    // Collect all Critical Dates that match ANY filter (OR logic)
    const matchingDates = new Set<CriticalDate>();

    filters.forEach(filter => {

      switch (filter) {
        case MyWorkFilter.PendingMyAction:
          // Add all dates where user is in assignedTo array AND actionCompleted is false
          let pendingMyActionCount = 0;
          dates.forEach(date => {
            if (this.isUserInAssignedTo(date, userEmail) && date.actionCompleted === false) {
              matchingDates.add(date);
              pendingMyActionCount++;
            }
          });
          // console.log(`Found ${pendingMyActionCount} dates pending user's action`);
          break;

        case MyWorkFilter.TaskingIAssigned:
          // Add all dates where user matches assignedBy AND actionCompleted is false
          let taskingIAssignedCount = 0;
          dates.forEach(date => {
            if (this.isUserAssignedBy(date, userEmail) && date.actionCompleted === false) {
              matchingDates.add(date);
              taskingIAssignedCount++;
            }
          });
          // console.log(`Found ${taskingIAssignedCount} dates where user assigned tasking`);
          break;

        default:
          break;
      }
    });

    const result = Array.from(matchingDates);
    // console.log(`Total matching Critical Dates (after OR): ${result.length}`);
    return result;
  }

  /**
   * Checks if the given user is in the assignedTo array of a Critical Date
   * Handles both email strings and user objects
   */
  private isUserInAssignedTo(date: CriticalDate, userEmail: string): boolean {
    if (!date.assignedTo || !Array.isArray(date.assignedTo) || date.assignedTo.length === 0) {
      return false;
    }

    const matchesUser = date.assignedTo.some((assignedUser: any) => {
      if (!assignedUser) return false;

      // Handle string case - direct email comparison
      if (typeof assignedUser === 'string') {
        return assignedUser === userEmail;
      }

      // Handle object case - check email or emailAddress property
      return assignedUser.email === userEmail ||
             assignedUser.emailAddress === userEmail ||
             assignedUser.userId === userEmail;
    });

    return matchesUser;
  }

  /**
   * Checks if the given user matches the assignedBy field of a Critical Date
   * Handles both email strings and user objects
   */
  private isUserAssignedBy(date: CriticalDate, userEmail: string): boolean {
    if (!date.assignedBy) {
      return false;
    }

    let matches = false;

    // Handle string case - direct email comparison
    if (typeof date.assignedBy === 'string') {
      matches = date.assignedBy === userEmail;
    } else {
      // Handle object case - check email or emailAddress property
      const assignedByObj = date.assignedBy as any;
      matches = assignedByObj.email === userEmail ||
                assignedByObj.emailAddress === userEmail ||
                assignedByObj.userId === userEmail;
    }

    return matches;
  }

  /**
   * Shared helper to check if a person/user field matches the current user's email.
   * Handles all storage formats:
   *   - Plain string (email)
   *   - Flat user object { email, userId, emailAddress }
   *   - Backend-hydrated user object { userId, userData: { email, ... } }
   */
  /**
   * Filters Provisions based on active filters and current user.
   * MVP: Owner only — Provision.owner is a plain email string, so there is no
   * multi-role ownership to unpick the way Contract/COI/BAA have. Other filters
   * fall through to the default and simply contribute nothing.
   */
  filterProvisions(provisions: Provision[]): Provision[] {
    const userEmail = this.currentUserEmail();
    const filters = this.activeFilters();

    if (!userEmail || filters.length === 0) {
      return [];
    }

    const matching = new Set<Provision>();

    filters.forEach(filter => {
      switch (filter) {
        case MyWorkFilter.Owner:
          provisions.forEach(provision => {
            if (this.matchesUserEmail(provision.owner, userEmail, 'owner')) {
              matching.add(provision);
            }
          });
          break;

        default:
          break;
      }
    });

    return Array.from(matching);
  }

  private matchesUserEmail(person: any, userEmail: string, fieldName: string): boolean {
    if (!person) return false;

    // Handle string case - direct email comparison
    if (typeof person === 'string') {
      return person === userEmail;
    }

    // Handle object case - check all possible email locations
    const matches =
      person.email === userEmail ||
      person.emailAddress === userEmail ||
      person.userId === userEmail ||
      person.userData?.email === userEmail;

    return matches;
  }
}
